<script setup>
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

// Array of { type: 'URL'|'QUICK_REPLY', text: String, url: String }.
// Mirrors exactly what Whatsapp::TemplateManagementService#build_button
// accepts - no client-only shape that would need translating server-side.
const buttons = defineModel('buttons', { type: Array, default: () => [] });

const { t } = useI18n();

// Whatsapp::TemplateManagementService::MAX_BUTTONS
const MAX_BUTTONS = 10;
// Whatsapp::TemplateManagementService::BUTTON_TEXT_MAX_LENGTH
const BUTTON_TEXT_MAX_LENGTH = 25;

const typeOptions = [
  {
    value: 'QUICK_REPLY',
    label: t('WHATSAPP_TEMPLATES.BUILDER.BUTTONS.QUICK_REPLY'),
  },
  { value: 'URL', label: t('WHATSAPP_TEMPLATES.BUILDER.BUTTONS.URL') },
];

const addButton = () => {
  if (buttons.value.length >= MAX_BUTTONS) return;
  buttons.value = [
    ...buttons.value,
    { type: 'QUICK_REPLY', text: '', url: '' },
  ];
};

const removeButton = index => {
  buttons.value = buttons.value.filter((_, i) => i !== index);
};

const updateButton = (index, key, value) => {
  buttons.value = buttons.value.map((button, i) =>
    i === index ? { ...button, [key]: value } : button
  );
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center justify-between">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('WHATSAPP_TEMPLATES.BUILDER.BUTTONS.LABEL') }}
      </label>
      <Button
        :label="t('WHATSAPP_TEMPLATES.BUILDER.BUTTONS.ADD')"
        variant="link"
        size="sm"
        icon="i-lucide-plus"
        :disabled="buttons.length >= MAX_BUTTONS"
        @click="addButton"
      />
    </div>

    <div
      v-for="(button, index) in buttons"
      :key="index"
      class="flex items-start gap-2 p-2 border rounded-lg border-n-weak"
    >
      <div class="flex flex-col flex-1 gap-2">
        <ComboBox
          :model-value="button.type"
          :options="typeOptions"
          @update:model-value="value => updateButton(index, 'type', value)"
        />
        <Input
          :model-value="button.text"
          :placeholder="
            t('WHATSAPP_TEMPLATES.BUILDER.BUTTONS.TEXT_PLACEHOLDER')
          "
          :message="
            button.text.length > BUTTON_TEXT_MAX_LENGTH
              ? t('WHATSAPP_TEMPLATES.BUILDER.BUTTONS.TEXT_TOO_LONG', {
                  max: BUTTON_TEXT_MAX_LENGTH,
                })
              : ''
          "
          message-type="error"
          @update:model-value="value => updateButton(index, 'text', value)"
        />
        <Input
          v-if="button.type === 'URL'"
          :model-value="button.url"
          type="url"
          :placeholder="t('WHATSAPP_TEMPLATES.BUILDER.BUTTONS.URL_PLACEHOLDER')"
          @update:model-value="value => updateButton(index, 'url', value)"
        />
      </div>
      <Button
        variant="ghost"
        size="sm"
        icon="i-lucide-trash-2"
        :aria-label="t('WHATSAPP_TEMPLATES.BUILDER.BUTTONS.REMOVE')"
        @click="removeButton(index)"
      />
    </div>

    <p v-if="!buttons.length" class="text-xs text-n-slate-11">
      {{ t('WHATSAPP_TEMPLATES.BUILDER.BUTTONS.EMPTY') }}
    </p>
  </div>
</template>
